import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Scrolls to the `#fragment` in the URL once the content it targets exists.
 *
 * Two things this has to get right, both of which the naive version got wrong:
 *
 * 1. **Wait for the content.** Sections are fetched, so on a cold load of
 *    `/about#how` the browser's own fragment scroll runs against an empty page
 *    and gives up. `ready` is the flag that says the sections are rendered.
 *
 * 2. **Scroll instantly, not smoothly.** `scrollIntoView` inherits the page's
 *    `scroll-behavior: smooth`, and a smooth scroll is cancelled by any layout
 *    shift while it is running — which is exactly what the hero and card
 *    images do as they decode. The jump silently landed back at the top. An
 *    instant scroll inside `requestAnimationFrame` (so layout has settled) is
 *    deterministic, and a deep link is a jump anyway: nobody asked to watch
 *    the page travel there.
 *
 * Clearing the header is handled in CSS by `scroll-margin-top` on the target,
 * not by arithmetic here, so the offset cannot drift from the sticky header's
 * real height.
 */
export function useHashScroll(ready) {
  const { hash } = useLocation();

  useEffect(() => {
    if (!ready || !hash) return;
    const target = document.getElementById(decodeURIComponent(hash.slice(1)));
    if (!target) return;

    // Two frames, not one.
    //
    // A single frame was enough while these pages were short. The legal
    // documents are now long enough that the browser is still laying out —
    // and, on a hard load, still running its own scroll restoration — when the
    // first frame fires, so the jump was computed against a stale layout and
    // then undone. The second frame runs after that has settled.
    let second;
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => {
        target.scrollIntoView({ block: 'start', behavior: 'instant' });
      });
    });
    return () => {
      cancelAnimationFrame(first);
      if (second) cancelAnimationFrame(second);
    };
  }, [ready, hash]);
}
